import {
  ChangeType,
  Comment,
  CommentEntityType,
  EntityType,
  Goal,
  LogEntry,
  Task,
  Thread,
} from '@lib/graphql/__generated__/graphql'
import { TASK_TYPES } from '@utils/constants'
import { getDateFromTimeStamp } from '@utils/Date'
import { isToDo } from '@utils/Task'
import { endOfDay } from 'date-fns'

export const commentEntityTypeMap = new Map([
  [CommentEntityType.Task, EntityType.Task],
  [CommentEntityType.Goal, EntityType.Goal],
])

export const isActivityLog = (data) => data?.__typename === 'LogEntry'

export const shouldShowLogEntry = (
  log: LogEntry,
  entityType: CommentEntityType,
  entity?: Task | Goal | Thread | null,
  allLogs?: LogEntry[],
) => {
  const newValue = log.newValue as any
  const oldValue = log.oldValue as any

  if (
    log.changeType === ChangeType.LabelChanged &&
    bothArrayValuesAreEmpty(newValue?.label, oldValue?.label)
  ) {
    return false
  }

  if (
    (log.changeType === ChangeType.AssigneeChanged ||
      log.changeType === ChangeType.OwnerChanged ||
      log.changeType === ChangeType.FollowerChanged) &&
    bothArrayValuesAreEmpty(newValue?.account, oldValue?.account)
  ) {
    return false
  }

  if (entityType === CommentEntityType.Task) {
    const rootTask = (entity as Task)?.taskTree?.find(
      (task) => task.type === TASK_TYPES.ROOT,
    )

    if (!rootTask) return true

    // If the log is not for the root task, show always
    if (rootTask.id !== log.entityId) return true

    // If root task has no assignees, show always
    if (rootTask?.assignees?.length === 0) return true

    // For To-Do: show logs before first assignee
    if (isToDo(rootTask)) {
      const firstAssigneeLog = allLogs
        ?.filter(
          (l) => l.isCreate && l.changeType === ChangeType.AssigneeChanged,
        )
        .sort((a, b) => a.createdAt.seconds - b.createdAt.seconds)[0]

      if (
        firstAssigneeLog &&
        log.createdAt.seconds < firstAssigneeLog.createdAt.seconds
      ) {
        return true
      }
    }

    // For root tasks with multiple assignees, show always
    if (rootTask?.assignees?.length > 1) {
      return true
    }

    // For all other cases (root task with 1 assignee), only show selected logs
    const allowedTypes = [
      ChangeType.TaskDeleted,
      ChangeType.FollowerChanged,
      ChangeType.OwnerChanged,
    ]

    if (!allowedTypes.includes(log.changeType)) {
      return false
    }
  }

  return true
}

export const groupCommentsByDate = (
  comments: (Comment | LogEntry)[],
  entity,
  entityType,
): Map<number, Comment[] | LogEntry[]> => {
  const { logs: allLogEntries, onlyComments: allComments } = comments.reduce(
    (acc, comment) => {
      if (isActivityLog(comment)) {
        acc.logs.push(comment as LogEntry)
      } else {
        acc.onlyComments.push(comment as Comment)
      }
      return acc
    },
    { logs: [] as LogEntry[], onlyComments: [] as Comment[] },
  )

  const uniqueLogs = getUniqueLogs(allLogEntries, entityType, entity)

  return [...allComments, ...uniqueLogs]
    .sort(commentsSorter)
    .reduce((acc, comment) => {
      const key = endOfDay(getDateFromTimeStamp(comment.createdAt)).getTime()
      if (!acc.has(key)) {
        acc.set(key, [])
      }
      acc.get(key)!.push(comment)
      return acc
    }, new Map<number, (Comment | LogEntry)[]>())
}

export const commentsSorter = (a, b) =>
  a.createdAt?.seconds - b.createdAt?.seconds

const bothArrayValuesAreEmpty = (
  newValueArray: any[],
  oldValueArray: any[],
): boolean => {
  return !newValueArray?.length && !oldValueArray?.length
}

const getUniqueLogs = (allLogEntries: LogEntry[], entityType, entity) => {
  let logs = allLogEntries
  if (entityType === EntityType.Task) {
    logs = combineTaskLogs(logs, entity)
  }

  const uniqueLogs = logs.reduce((acc, log: LogEntry) => {
    if (!log.isCreate) {
      acc.push(log)
      return acc
    }

    const existingCreateLog = acc.find((item) => {
      return item.entityId === log.entityId && item.isCreate
    })

    if (existingCreateLog) return acc

    const assigneeLog = logs
      .filter((item) => item.entityId === log.entityId && item.isCreate)
      .find((i) => i.changeType === ChangeType.AssigneeChanged)

    if (assigneeLog) {
      acc = acc.filter(
        (item) => item.entityId !== log.entityId || !item.isCreate,
      )

      acc.push(assigneeLog)
    } else {
      // For the case when there is not assignee when creating an item (eg. Goal, To-Do)
      const ownerLog = logs
        .filter((item) => item.entityId === log.entityId && item.isCreate)
        .find((i) => i.changeType === ChangeType.OwnerChanged)
      if (ownerLog) {
        acc = acc.filter(
          (item) => item.entityId !== log.entityId || !item.isCreate,
        )
        acc.push(ownerLog)
      }
    }

    return acc
  }, [])

  return uniqueLogs
}

const logChangesTypesToCombine = [
  ChangeType.AssigneeChanged,
  ChangeType.OwnerChanged,
  ChangeType.StatusChanged,
  ChangeType.ExecutionDateChanged,
  ChangeType.DeadlineChanged,
  ChangeType.FollowerChanged,
]

const TIME_THRESHOLD_SECONDS = 2

const combineTaskLogs = (logs, entity) => {
  const rootTaskId = entity.rootTaskId || entity.id

  const { nonCombinableLogs, duplicateTaskLogs, rootTaskLogs } = logs.reduce(
    (acc, log) => {
      if (logChangesTypesToCombine.includes(log.changeType)) {
        if (log.entityId === rootTaskId) {
          acc.rootTaskLogs.push(log)
        } else {
          acc.duplicateTaskLogs.push(log)
        }
      } else {
        acc.nonCombinableLogs.push(log)
      }
      return acc
    },
    {
      nonCombinableLogs: [],
      duplicateTaskLogs: [],
      rootTaskLogs: [],
    },
  )

  const combinedLogs = [...duplicateTaskLogs]
  rootTaskLogs.forEach((rootLog) => {
    if (
      !duplicateTaskLogs.some(
        (dupLog) =>
          dupLog.changeType === rootLog.changeType &&
          Math.abs(dupLog.createdAt.seconds - rootLog.createdAt.seconds) <=
            TIME_THRESHOLD_SECONDS,
      )
    ) {
      combinedLogs.push(rootLog)
    }
  })

  return [...nonCombinableLogs, ...combinedLogs]
}
